import type { IThemePagesMap, IThemePage } from '~/stores/themePage';
import type { Campaign, Variant } from '~/modules/campaign/types';
import type { GVariantBadgeProps } from '~/presentation/ui';
import type { TemplateType } from '~/types/template';

import { parseTemplateIdBy, parseTemplateInfoBy } from '~/utils';
import { ProviderSource } from '~/types/template';

import { IVariantType } from '~/modules/campaign/types';
import { GVariantBadgeTone } from '~/presentation/ui';

export const findVariantByOrigin = <TData extends Variant = Variant>(
  campaign: Campaign | undefined,
  isOrigin: boolean,
): TData | undefined => {
  return campaign?.variants?.find((v) => v?.isOrigin === isOrigin) as TData;
};
export const findVariantByType = <TData extends Variant = Variant>(
  campaign: Campaign | undefined,
  variantType: IVariantType,
): TData | undefined => {
  switch (variantType) {
    case IVariantType.VARIANT_A:
      return findVariantByOrigin(campaign, true) as TData;
    case IVariantType.VARIANT_B:
      return findVariantByOrigin(campaign, false) as TData;
    default:
      return undefined;
  }
};
export const findVariantIndxByOrigin = (campaign: Campaign | undefined, isOrigin: boolean) => {
  return campaign?.variants?.findIndex((v) => v?.isOrigin === isOrigin);
};
export const findVariantByIndex = (
  campaign: Campaign | undefined,
  index: number,
): Variant | undefined => {
  return campaign?.variants?.[index];
};
export const isOriginVariant = (variant: Variant | undefined): boolean => {
  return !!variant?.isOrigin;
};
export const getVariantType = (variant: Variant | undefined): IVariantType => {
  return variant?.isOrigin ? IVariantType.VARIANT_A : IVariantType.VARIANT_B;
};
export const getVariantTone = (variant: Variant | undefined): GVariantBadgeProps['tone'] => {
  return variant?.isOrigin ? GVariantBadgeTone.ACTIVE : GVariantBadgeTone.INACTIVE;
};
export const getVariantToneByType = (variantType: IVariantType): GVariantBadgeProps['tone'] => {
  return variantType === IVariantType.VARIANT_A
    ? GVariantBadgeTone.ACTIVE
    : GVariantBadgeTone.INACTIVE;
};

export const getVariantLabelByType = (variantType: IVariantType): string => {
  return variantType === IVariantType.VARIANT_A ? 'Control' : 'Variant';
};
export const getVariantPageType = (variant: Variant | undefined): TemplateType | undefined => {
  if (!variant) return;
  return variant.templateType as TemplateType;
};

export const getVariantPath = (variant: Variant | undefined): string | undefined => {
  if (!variant) return;

  const url = variant.template?.redirectURL;
  if (!url) return;

  try {
    return new URL(url).pathname;
  } catch (error) {
    console.error(error);
    return;
  }
};

export const isVariantSamePath = (
  variant: Variant | undefined,
  path: string | undefined,
): boolean => {
  if (!variant || !path) return false;

  return getVariantPath(variant) === path;
};

export const getThemePageIDBySuffix = (templateSuffix: string | undefined): string => {
  if (!templateSuffix) return '0';

  const templateInfo = parseTemplateInfoBy({
    templateSuffix,
  });

  if (templateInfo?.providerSource === ProviderSource.GEMPAGESV7) {
    return parseTemplateIdBy(templateInfo.suffix, ProviderSource.GEMPAGESV7) ?? '0';
  }

  return '0';
};

export const validThemePageID = (themePageID: string | undefined): boolean => {
  return !!themePageID && themePageID !== '0';
};

export const getThemePageByTemplateSuffix = (
  templateSuffix: string | undefined,
  allThemePagesMap: IThemePagesMap,
): IThemePage | undefined => {
  if (!templateSuffix) return undefined;

  const themePageId = getThemePageIDBySuffix(templateSuffix);
  return allThemePagesMap[themePageId];
};
